import { useTranslations } from '@vocab/react';
import {
  Stack,
  TabsProvider,
  Tabs,
  Tab,
  TabPanels,
  TabPanel,
} from 'braid-design-system';
import type { FunctionComponent } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import {
  GUIDED_WRITING_PRESSED,
  STANDARD_WRITING_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateIsGuided } from 'src/app/v1/state/modules/formData/isGuided';
import type { AppState } from 'src/types/appState';
import type {
  GuidedWritingFlowRef,
  StandardWritingFlowRef,
} from 'src/types/formFields';
import type { TrackingEvent } from 'src/types/trackingData';

import { GuidedWritingFlow } from '../GuidedWritingFlow';
import { StandardWritingFlow } from '../StandardWritingFlow';

import translations from './.vocab';

enum WritingModes {
  STANDARD = 'standard',
  GUIDED = 'guided',
}

interface AdWritingTogglerProps {
  standardWritingFlowRef: React.Ref<StandardWritingFlowRef>;
  guidedWritingFlowRef: React.Ref<GuidedWritingFlowRef>;
}

export const AdWritingToggler: FunctionComponent<AdWritingTogglerProps> = (
  props: AdWritingTogglerProps,
) => {
  const { isGuided } = useSelector(
    (state: AppState) => ({
      isGuided: state.formData?.isGuided,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const { standardWritingFlowRef, guidedWritingFlowRef } = props;
  const { t } = useTranslations(translations);

  const handleOnChange = (_selectedValue: number, selectedItem?: string) => {
    const selectedIsGuided = selectedItem === WritingModes.GUIDED;
    dispatch(actionUpdateIsGuided(selectedIsGuided));

    sendTrackingData(selectedIsGuided);
  };

  const sendTrackingData = (selectedIsGuided: boolean) => {
    const eventName = selectedIsGuided
      ? GUIDED_WRITING_PRESSED
      : STANDARD_WRITING_PRESSED;

    const eventData: TrackingEvent = {
      eventName,
    };

    getTrackingAdapter.sendLinkEvent(eventData);
  };

  return (
    <TabsProvider
      id="write-type"
      onChange={handleOnChange}
      selectedItem={isGuided ? WritingModes.GUIDED : WritingModes.STANDARD}
    >
      <Stack space="medium">
        <Tabs label="write-type">
          <Tab data={{ cy: 'standardWritingBtn' }} item={WritingModes.STANDARD}>
            {t('standard-writing-button-label')}
          </Tab>
          <Tab data={{ cy: 'guidedWritingBtn' }} item={WritingModes.GUIDED}>
            {t('guided-writing-button-label')}
          </Tab>
        </Tabs>
        <TabPanels>
          <TabPanel>
            <StandardWritingFlow ref={standardWritingFlowRef} />
          </TabPanel>
          <TabPanel>
            <GuidedWritingFlow ref={guidedWritingFlowRef} />
          </TabPanel>
        </TabPanels>
      </Stack>
    </TabsProvider>
  );
};
